import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';
import { useConfig } from './config';
import { checkPluginStatus, getUnreadSummary } from '../services/moodle/chat';
import { useLocation } from 'react-router-dom';

export const NotificationContext = createContext(null);

const UNREAD_POLL_INTERVAL_MS = 35000;
const REFOCUS_DEBOUNCE_MS = 5000;

export const NotificationProvider = ({ children }) => {
  const { user } = useAuth();
  const { config } = useConfig();
  const location = useLocation();

  const [totalUnread, setTotalUnread] = useState(0);
  const [coursesWithUnread, setCoursesWithUnread] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pluginEnabled, setPluginEnabled] = useState(false);

  const lastFetchRef = useRef(0);
  const pollTimerRef = useRef(null);
  const isMountedRef = useRef(true);

  const fetchUnread = useCallback(async () => {
    if (!user || !user.token || !config?.baseURL) {
      setPluginEnabled(false);
      setTotalUnread(0);
      setCoursesWithUnread([]);
      return;
    }

    lastFetchRef.current = Date.now();
    try {
      setLoading(true);

      // Notifications use the same plugin gate as the course chat.
      const isPluginEnabled = await checkPluginStatus(user.token, config.baseURL);
      if (!isPluginEnabled) {
        if (isMountedRef.current) {
          setPluginEnabled(false);
          setTotalUnread(0);
          setCoursesWithUnread([]);
          setError(null);
        }
        return;
      }

      if (isMountedRef.current) {
        setPluginEnabled(true);
      }

      const data = await getUnreadSummary(user.token, config.baseURL);
      if (isMountedRef.current) {
        setTotalUnread(data.totalunread || 0);
        setCoursesWithUnread(data.courses || []);
        setError(null);
      }
    } catch (err) {
      if (isMountedRef.current) {
        setPluginEnabled(false);
        setTotalUnread(0);
        setCoursesWithUnread([]);
        setError(err.message || 'Error fetching notifications');
      }
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  }, [user, config]);

  // Initial fetch and on route change
  useEffect(() => {
    isMountedRef.current = true;
    fetchUnread();

    return () => {
      isMountedRef.current = false;
    };
  }, [fetchUnread, location.pathname]);

  const recentDedupeRef = useRef(new Map());

  // Listen for message read events from ChatContext for instant optimistic decrement
  useEffect(() => {
    const handleMessageRead = (event) => {
      const { courseId, unreadCount } = event.detail || {};
      const countToSubtract = unreadCount || 1;
      const now = Date.now();

      const lastTime = recentDedupeRef.current.get(Number(courseId)) || 0;
      if (now - lastTime > 2500) {
        recentDedupeRef.current.set(Number(courseId), now);

        // 1. Optimistically decrement badge and course count immediately
        setTotalUnread((prev) => Math.max(0, prev - countToSubtract));
        setCoursesWithUnread((prevCourses) =>
          prevCourses
            .map((c) => {
              if (Number(c.courseid) === Number(courseId)) {
                const newCount = Math.max(0, (c.unreadcount || 0) - countToSubtract);
                return { ...c, unreadcount: newCount };
              }
              return c;
            })
            .filter((c) => c.unreadcount > 0)
        );
      }

      // 2. Re-fetch from server after backend markRead settles in DB
      setTimeout(() => {
        if (isMountedRef.current) {
          fetchUnread();
        }
      }, 1500);
    };

    window.addEventListener('tipddy:message_read', handleMessageRead);
    return () => {
      window.removeEventListener('tipddy:message_read', handleMessageRead);
    };
  }, [fetchUnread]);

  // Polling loop + Page Visibility API (pause when hidden)
  useEffect(() => {
    if (!user || !user.token || !config?.baseURL) {
      return;
    }

    const schedulePoll = () => {
      clearTimeout(pollTimerRef.current);
      if (document.hidden) {
        return;
      }
      pollTimerRef.current = setTimeout(async () => {
        await fetchUnread();
        schedulePoll();
      }, UNREAD_POLL_INTERVAL_MS);
    };

    schedulePoll();

    const handleVisibilityChange = () => {
      if (!document.hidden) {
        const elapsed = Date.now() - lastFetchRef.current;
        if (elapsed > REFOCUS_DEBOUNCE_MS) {
          fetchUnread();
        }
        schedulePoll();
      } else {
        clearTimeout(pollTimerRef.current);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearTimeout(pollTimerRef.current);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [user, config, fetchUnread]);

  const value = {
    totalUnread,
    coursesWithUnread,
    loading,
    error,
    pluginEnabled,
    refreshUnread: fetchUnread,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    return {
      totalUnread: 0,
      coursesWithUnread: [],
      loading: false,
      error: null,
      pluginEnabled: false,
      refreshUnread: () => {},
    };
  }
  return context;
};
